import { formatDateToISOString } from '../../shared/helpers/date.func';
import { ApiAdmissionStatus } from '../api-types/admission-status.api';
import { ApiCareer, ApiCareerCreate, ApiCareerUpdate } from '../api-types/career.api';
import { ApiSocialSecurityStatus } from '../api-types/social-security-status.api';
import { Career, CreateCareer, UpdateCareer } from '../app-types/career.app';
import { ParticipationStatus } from '../app-types/participation-status.app';
import { SocialSecurityStatus } from '../app-types/social-security-status.app';
import { transformApiCountyToAppCounty } from './county.transform';
import { transformApiWorkplaceJobToAppWorkplaceJob } from './workplace-transform';

export const transformApiCareerToAppCareer = (input: ApiCareer): Career => {
	const output: Career = {
		id: input.id,
		remarkOfTheCareerStage: input.remarkOfTheCareerStage,
		remark: input.remark,
		startOfCareerStage: new Date(input.startOfCareerStage),
		endOfCareerStage: input.endOfCareerStage ? new Date(input.endOfCareerStage) : undefined,
		participationStatus: transformApiAdmissionStatusToAppParticipationStatus(input.admissionStatus),
		socialSecurityStatus: transformApiSocialSecurityStatusToAppSocialSecurityStatus(input.socialSecurityStatus),
		workplacesJob: input.workplace ? transformApiWorkplaceJobToAppWorkplaceJob(input.workplace) : undefined,
		isFrozen: input.isFrozen,
		cityDistrict: input.categoryCityDistrict ? transformApiCountyToAppCounty(input.categoryCityDistrict) : undefined,
		careerOrientationStartDate: input.careerOrientationStartDate
			? new Date(input.careerOrientationStartDate)
			: undefined,
	};
	return output;
};

export const transformApiCareersToAppCareers = (input: ApiCareer[]): Career[] => {
	return input.map(transformApiCareerToAppCareer);
};

export const transformApiAdmissionStatusToAppParticipationStatus = (input: ApiAdmissionStatus): ParticipationStatus => {
	const output: ParticipationStatus = {
		id: input.id,
		remark: input.remark,
		guid: input.guid,
		initial: input.initial,
	};
	return output;
};

export const transformApiAdmissionStatusesToAppParticipationStatuses = (
	input: ApiAdmissionStatus[],
): ParticipationStatus[] => {
	return input.map(transformApiAdmissionStatusToAppParticipationStatus);
};

export const transformApiSocialSecurityStatusToAppSocialSecurityStatus = (
	input: ApiSocialSecurityStatus,
): SocialSecurityStatus => {
	const output: SocialSecurityStatus = {
		id: input.id,
		remark: input.remark,
		guid: input.guid,
		initial: input.initial,
		statusActivity: input.statusActivity,
	};

	return output;
};

export const transformApiSocialSecurityStatusesToAppSocialSecurityStatuses = (
	input: ApiSocialSecurityStatus[],
): SocialSecurityStatus[] => {
	return input.map(transformApiSocialSecurityStatusToAppSocialSecurityStatus);
};

export const transformCareerCreateToApiCareerCreate = (
	input: CreateCareer,
	clientId: string | number,
): ApiCareerCreate => {
	const output: ApiCareerCreate = {
		socialSecurityStatusId: input.socialSecurityStatusId,
		startOfCareerStage: formatDateToISOString(input.startOfCareerStage) || '',
		remark: input.remark,
		remarkOfTheCareerStage: input.remarkOfTheCareerStage,
	};
	if (input.dynamicFields) {
		output.workplace = {
			clientId: Number(clientId),
			institutionBranchId: input.dynamicFields.branchId,
			institutionInformed: input.dynamicFields.institutionInformed,
			institutionPersonId: input.dynamicFields.personId,
			occupationTitle: input.dynamicFields.jobTitle,
			remark: input.dynamicFields.remark,
			typeAndScopeOfTheWorkplace: input.dynamicFields.typeAndScopeOfTheWorkplace,
		};
		output.categoryCityDistrictId = input.dynamicFields.cityDistrictId;
	}
	if (input.dynamicFields && input.dynamicFields.careerOrientationStartYear) {
		// Hard coding the middle of the year as only year is relevant but backend wants a ISO date string ¯\_(ツ)_/¯
		output.careerOrientationStartDate = new Date(
			`${input.dynamicFields.careerOrientationStartYear}-07-01`,
		).toISOString();
	}

	return output;
};

export const transformUpdateCareerToApiCareerUpdate = (input: UpdateCareer): ApiCareerUpdate => {
	const output: ApiCareerUpdate = {
		admissionStatusId: input.admissionId,
		startOfCareerStage: input.startOfCareerStage ? input.startOfCareerStage.getISODateStringWithoutTime() : undefined,
		remarkOfTheCareerStage: input.remarkOfTheCareerStage,
	};
	return output;
};

/**
 * Maps the name of a control in our form to the corresponding field name expected by the API for career.
 * @param fieldName Name of the field inside our forms
 * @returns name of the field coming from the API
 */
export const mapEditFormFieldNamesToApiCareerFieldNames = (fieldName: string): string => {
	const fieldMapping: Record<string, string> = {
		date: 'StartOfCareerStage',
		remarkOfTheCareerStage: 'RemarkOfTheCareerStage',
		admissionId: 'AdmissionStatus',
	};
	return fieldMapping[fieldName] || fieldName;
};
